import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { mockOrders, Order } from '../data/mockOrders';
import { useAuth } from './AuthContext';
import DataService from '../services/dataService';

interface OrderContextType {
  orders: Order[];
  addOrder: (order: Order) => Promise<void>;
  getOrderById: (id: string) => Order | undefined;
  updateOrderStatus: (id: string, status: Order['status']) => Promise<void>;
  refreshOrders: () => Promise<void>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Helper to transform DB order to App Order
  // Types for data coming from the database
  interface DbProduct {
    name: string;
    image: string;
  }

  interface DbOrderItem {
    id: string;
    product_id: string;
    price: number;
    quantity: number;
    product?: DbProduct;
  }

  interface DbOrder {
    id: string;
    created_at: string;
    status: Order['status'];
    total_amount: number;
    order_items?: DbOrderItem[];
    shipping_address: string | { street?: string; city?: string; };
    payment_method: string;
  }

  // Helper to transform DB order to App Order
  const transformDbOrder = (dbOrder: DbOrder): Order => {
    return {
      id: dbOrder.id,
      date: dbOrder.created_at,
      status: dbOrder.status,
      total: dbOrder.total_amount,
      items: dbOrder.order_items?.map((item) => ({
        id: item.id,
        productId: item.product_id,
        name: item.product?.name || 'Unknown Product',
        price: item.price,
        quantity: item.quantity,
        image: item.product?.image || 'https://via.placeholder.com/150'
      })) || [],
      // Handle shipping address safely (could be JSON or string)
      shippingAddress: typeof dbOrder.shipping_address === 'object' 
        ? `${dbOrder.shipping_address.street || ''}, ${dbOrder.shipping_address.city || ''}`
        : dbOrder.shipping_address || '',
      paymentMethod: dbOrder.payment_method || 'Unknown',
      trackingNumber: dbOrder.id
    };
  };

  const refreshOrders = async () => {
    if (!user) {
      // Fallback to local storage if not logged in
      const savedOrders = localStorage.getItem('farmers_hub_orders');
      if (savedOrders) {
        try {
          setOrders(JSON.parse(savedOrders));
        } catch (e) {
          setOrders(mockOrders);
        }
      } else {
        setOrders(mockOrders);
      }
      return;
    }

    try {
      const dbOrders = await DataService.getUserOrders(user.id);
      if (dbOrders && dbOrders.length > 0) {
        setOrders(dbOrders.map(transformDbOrder));
      } else {
        // If DB is empty, maybe we still want to show mock data for demo?
        // Or strictly empty. Let's stick to empty if logged in but no orders.
        setOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      // Fallback
      setOrders(mockOrders);
    }
  };

  // Initial Load
  useEffect(() => {
    refreshOrders().finally(() => setInitialized(true));
  }, [user]);

  // Sync to local storage as backup (optional, or for guest mode)
  useEffect(() => {
    if (initialized && !user) {
      localStorage.setItem('farmers_hub_orders', JSON.stringify(orders));
    }
  }, [orders, initialized, user]);

  const addOrder = async (order: Order) => {
    if (user) {
      // In a real app, 'addOrder' usually happens via Checkout flow calling API directly.
      // But if we call this from client side helper:
      // We assume the API call is handled elsewhere (e.g. Checkout.tsx) 
      // OR we implement it here.
      // Since Checkout.tsx likely calls this, let's implement the DB save here if needed,
      // OR just refresh.
      // For now, optimistically update state
      setOrders((prev) => [order, ...prev]);
    } else {
      setOrders((prev) => [order, ...prev]);
    }
  };

  const getOrderById = (id: string) => {
    return orders.find((o) => o.id === id);
  };

  const updateOrderStatus = async (id: string, status: Order['status']) => {
    // Optimistic update
    setOrders((prev) =>
      prev.map((order) => (order.id === id ? { ...order, status } : order))
    );

    if (user) {
      try {
        await DataService.updateOrderStatus(id, status);
      } catch (error) {
        console.error('Failed to update order status in DB', error);
        // Revert? (Complex without previous state, let's assume success or reload)
        refreshOrders();
      }
    }
  };

  return (
    <OrderContext.Provider value={{ orders, addOrder, getOrderById, updateOrderStatus, refreshOrders }}>
      {children}
    </OrderContext.Provider>
  );
};
